import { Button, Grid, Paper } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import MainContentHeader from 'src/shared/components/atoms/mainContentHeader';
import PageTabNavigation, { PageTabItem } from 'src/shared/components/navigations/pageTabNavigation';
import GridWrapper from 'src/shared/components/organisms/gridWrapper';
import { useInvoiceContext } from 'src/shared/contexts/invoiceContext';
import { RoutesEnum } from 'src/shared/models/enums/routes.enum';
import { useState } from 'react';
import { AddCircle } from '@mui/icons-material';
import { useGlobals } from 'src/shared/contexts/appContext';
import { getSysProducts } from 'src/shared/functions/getSysProducts';
import { getPermission } from 'src/shared/functions/getSysProductPermissions';
import { PermissionEnum } from 'src/shared/models/enums/permissionEnums';
import { SysProductYvNameEnum } from 'src/shared/models/enums/sysProductEnums';
import { AutogiroMandateCreateFilesModal } from '../components/autogiroMandateCreateFilesModal';
import { AutogiroMandateCreateModal } from '../components/autogiroMandateCreateModal';
import { useAutogiroUnlinkedBadge } from '../hooks/useAutogiroUnlinkedBadge';
import { useActionRequiredBadge } from '../hooks/useActionRequiredBadge';
import { useDonorActionRequiredBadge } from '../hooks/useDonorActionRequiredBadge';

export const AutogiroPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currentUser, setDateStamp } = useGlobals();
  const location = useLocation();
  const isAutogiroPage = location.pathname === RoutesEnum.Autogiro;
  const { createFileMode, selectedAutogiroMandates, setCreateFileMode, setSelectedAutogiroMandates } = useInvoiceContext();
  const [showCreateAutogiroMandateModal, setShowCreateAutogiroMandateModal] = useState<boolean>();

  // Fliken för månadsgivare visas bara för de kunder som har insamling. Utan den produkten
  // finns inga givare, och då ska sidan se ut som den alltid gjort. Listan hämtas från
  // givarregistret och kräver därför också läsrätt till insamling.
  const hasFundraiser =
    getSysProducts(currentUser).some((x) => x.yvName.toLowerCase() === SysProductYvNameEnum.Fundraiser.toLowerCase()) &&
    getPermission(currentUser, PermissionEnum.FundraiserRead);

  // Siffrorna visas även för den som inte öppnat listan, annars upptäcks ett makulerat
  // medgivande först när dragningen uteblir. Samma hookar används i listorna, så att siffran
  // på fliken och antalet i filtret alltid är samma tal.
  // Varje fliks siffra är antalet rader som dess åtgärdsfilter visar. På Medgivanden är det de
  // ej kopplade, alltså medgivanden som ingen medlem eller givare hänger ihop med.
  const [unlinkedCount] = useAutogiroUnlinkedBadge();
  const [actionRequiredCount] = useActionRequiredBadge();
  const [donorActionRequiredCount] = useDonorActionRequiredBadge(hasFundraiser);

  const pageTabs: PageTabItem[] = [
    {
      label: 'invoice:autogiro.tabAutogiro',
      route: RoutesEnum.Autogiro,
      badgeCount: unlinkedCount,
    },
    // {
    //   label: 'invoice:autogiro.tabCreateFile',
    //   route: RoutesEnum.AutogiroCreateFile,
    // },
    {
      label: 'invoice:autogiro.tabMemberautogirostatus',
      route: RoutesEnum.Memberautogirostatus,
      badgeCount: actionRequiredCount,
    },
  ];

  if (hasFundraiser) {
    pageTabs.push({
      label: 'invoice:autogiro.tabDonorAutogiro',
      route: RoutesEnum.DonorAutogiro,
      badgeCount: donorActionRequiredCount,
    });
  }

  const onSaveHandler = () => {
    setCreateFileMode(false);
    setSelectedAutogiroMandates([]);
    navigate(RoutesEnum.Autogiro);
  };

  return (
    <>
      <GridWrapper>
        <Grid item xs>
          <MainContentHeader title={t('invoice:autogiroOverviewPage.pageTitle')} />
        </Grid>
        {isAutogiroPage && (
          <Grid item xs='auto'>
            <Button
              onClick={() => setShowCreateAutogiroMandateModal(true)}
              variant='contained'
              color='primary'
              startIcon={<AddCircle />}
              disableElevation
            >
              {t('invoice:autogiroOverviewPage.createAutogiroMandateButton')}
            </Button>
          </Grid>
        )}
        <Grid item xs={12}>
          <Paper>
            <PageTabNavigation pageTabs={pageTabs} />
            <Outlet />
          </Paper>
        </Grid>
      </GridWrapper>
      {createFileMode && selectedAutogiroMandates && !!selectedAutogiroMandates.length && (
        <AutogiroMandateCreateFilesModal
          mandates={selectedAutogiroMandates}
          modalOpen={selectedAutogiroMandates && !!selectedAutogiroMandates.length}
          onClose={() => {
            setCreateFileMode(false);
            setSelectedAutogiroMandates([]);
          }}
          onSave={onSaveHandler}
        />
      )}
      {showCreateAutogiroMandateModal && (
        <AutogiroMandateCreateModal
          modalOpen={showCreateAutogiroMandateModal}
          onClose={() => {
            setShowCreateAutogiroMandateModal(false);
          }}
          onSave={() => {
            setShowCreateAutogiroMandateModal(false);
            setDateStamp(new Date());
          }}
        />
      )}
    </>
  );
};
